import {Centrifuge, ServerPublicationContext} from 'centrifuge';
import {camelizeKeys} from 'humps';
import {noop} from 'lodash';
import {Dispatch, MutableRefObject, SetStateAction, useEffect} from 'react';

import {isRecord} from '../../../util/object';

import {applyReviewPresencePublication, removeExpiredReviewLocks} from './review-realtime-lock-store';
import {
    REVIEW_PRESENCE_HEARTBEAT_MS,
    REVIEW_PRESENCE_LOCK_TTL_MS,
    REVIEW_PRESENCE_SWEEPER_MS,
} from './review-realtime-presence-const';
import {isBrandPresenceChannel} from './review-realtime-presence-helper';
import {
    ReviewPresenceActivityOptionsType,
    ReviewPresenceEventEnum,
    reviewPresenceLockPublicationSchema,
    reviewPresenceUpdatePublicationSchema,
    ReviewPresenceUpdatePublicationType,
    ReviewRealtimeLockMapType,
} from './review-realtime-presence-type';
import {logReviewPresenceParseFailure} from './review-realtime-update-helper';

type PublishPresenceEventType = (
    options: ReviewPresenceActivityOptionsType & {
        event: ReviewPresenceEventEnum.Lock | ReviewPresenceEventEnum.Unlock | ReviewPresenceEventEnum.Heartbeat;
    }
) => Promise<void>;

type HandleReviewPresencePublicationOptionsType = {
    canReceiveForBrand: (brandId: number) => boolean;
    onReviewUpdate?: (payload: ReviewPresenceUpdatePublicationType) => void;
    publicationContext: ServerPublicationContext;
    sessionIdRef: MutableRefObject<string>;
    setLocksByReviewId: Dispatch<SetStateAction<ReviewRealtimeLockMapType>>;
};

function getPublicationBrandId(camelizedData: Record<string, unknown>): number | null {
    return typeof camelizedData.brandId === 'number' ? camelizedData.brandId : null;
}

// eslint-disable-next-line max-statements
function handleReviewPresencePublication(options: HandleReviewPresencePublicationOptionsType): void {
    const {canReceiveForBrand, onReviewUpdate, publicationContext, sessionIdRef, setLocksByReviewId} = options;

    if (!isBrandPresenceChannel(publicationContext.channel)) {
        return;
    }

    const camelizedData = camelizeKeys(publicationContext.data);

    if (!isRecord(camelizedData)) {
        return;
    }

    const brandId = getPublicationBrandId(camelizedData);

    if (brandId === null || !canReceiveForBrand(brandId)) {
        return;
    }

    if (camelizedData.event === ReviewPresenceEventEnum.Update) {
        const updateParseResult = reviewPresenceUpdatePublicationSchema.safeParse(camelizedData);

        if (!updateParseResult.success) {
            logReviewPresenceParseFailure('receive-update', updateParseResult.error);

            return;
        }

        if (updateParseResult.data.sessionId === sessionIdRef.current) {
            return;
        }

        onReviewUpdate?.(updateParseResult.data);

        return;
    }

    const lockParseResult = reviewPresenceLockPublicationSchema.safeParse(camelizedData);

    if (!lockParseResult.success) {
        logReviewPresenceParseFailure('receive-lock', lockParseResult.error);

        return;
    }

    setLocksByReviewId((previousLocks) => applyReviewPresencePublication(previousLocks, lockParseResult.data));
}

type UseReviewRealtimePresenceRuntimeHookOptionsType = {
    activeActivitiesRef: MutableRefObject<Array<ReviewPresenceActivityOptionsType>>;
    canReceiveAny: boolean;
    canReceiveForBrand: (brandId: number) => boolean;
    canSendForBrand: (brandId: number) => boolean;
    centrifuge: Centrifuge | null;
    onReviewUpdate?: (payload: ReviewPresenceUpdatePublicationType) => void;
    publishPresenceEvent: PublishPresenceEventType;
    sessionIdRef: MutableRefObject<string>;
    setLocksByReviewId: Dispatch<SetStateAction<ReviewRealtimeLockMapType>>;
    stopAllActivities: () => void;
};

export function useReviewRealtimePresenceRuntimeHook(options: UseReviewRealtimePresenceRuntimeHookOptionsType): void {
    const {
        activeActivitiesRef,
        canReceiveAny,
        canReceiveForBrand,
        canSendForBrand,
        centrifuge,
        onReviewUpdate,
        publishPresenceEvent,
        sessionIdRef,
        setLocksByReviewId,
        stopAllActivities,
    } = options;

    useEffect(() => {
        if (!canReceiveAny) {
            return noop;
        }

        return () => {
            stopAllActivities();
        };
    }, [canReceiveAny, stopAllActivities]);

    useEffect(() => {
        if (!canReceiveAny) {
            return noop;
        }

        function handleBeforeUnload(): void {
            stopAllActivities();
        }

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [canReceiveAny, stopAllActivities]);

    useEffect(() => {
        if (!canReceiveAny) {
            return noop;
        }

        const heartbeatIntervalId = window.setInterval(() => {
            activeActivitiesRef.current.forEach((activity) => {
                if (!canSendForBrand(activity.brandId)) {
                    return;
                }

                publishPresenceEvent({...activity, event: ReviewPresenceEventEnum.Heartbeat}).catch(() => {
                    /* ignore publish errors */
                });
            });
        }, REVIEW_PRESENCE_HEARTBEAT_MS);

        return () => {
            window.clearInterval(heartbeatIntervalId);
        };
    }, [activeActivitiesRef, canReceiveAny, canSendForBrand, publishPresenceEvent]);

    useEffect(() => {
        if (!canReceiveAny) {
            return noop;
        }

        const sweeperIntervalId = window.setInterval(() => {
            setLocksByReviewId((previousLocks) =>
                removeExpiredReviewLocks(previousLocks, Date.now(), REVIEW_PRESENCE_LOCK_TTL_MS)
            );
        }, REVIEW_PRESENCE_SWEEPER_MS);

        return () => {
            window.clearInterval(sweeperIntervalId);
        };
    }, [canReceiveAny, setLocksByReviewId]);

    useEffect(() => {
        if (!centrifuge || !canReceiveAny) {
            return noop;
        }

        function handlePublication(publicationContext: ServerPublicationContext): void {
            handleReviewPresencePublication({
                canReceiveForBrand,
                onReviewUpdate,
                publicationContext,
                sessionIdRef,
                setLocksByReviewId,
            });
        }

        centrifuge.on('publication', handlePublication);

        return () => {
            centrifuge.off('publication', handlePublication);
        };
    }, [canReceiveAny, canReceiveForBrand, centrifuge, onReviewUpdate, sessionIdRef, setLocksByReviewId]);
}
